import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { PublicRelationsListBoard } from '@api/BoardSDK';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';

const COLS = ['contactName', 'contactTitle', 'email', 'website', 'type', 'coverage', 'region', 'outreachStatus', 'lastContacted', 'notes'] as const;
export const PRESS_STATUSES = ['Not Contacted', 'Letter Sent', 'Replied', 'Covered Us', 'Bad Email'] as const;

export const getPressList = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), q: z.string().optional(), status: z.enum(PRESS_STATUSES).optional(), cursor: z.string().optional() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(async () => {
      const b = new PublicRelationsListBoard();
      if (data.cursor) return b.items().withColumns([...COLS]).withPagination({ limit: 100, cursor: data.cursor }).execute();
      const where: Record<string, unknown> = { removed: false };
      if (data.q?.trim()) where.name = { contains: data.q.trim() };
      if (data.status) where.outreachStatus = data.status;
      return b.items().withColumns([...COLS]).where(where as never).orderBy({ column: 'name', direction: 'asc' }).withPagination({ limit: 100 }).execute();
    });
  });

export const getPressCounts = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(() => new PublicRelationsListBoard().aggregate().groupBy('outreachStatus').where({ removed: false }).countItems('n').execute());
  });

export const PRESS_TYPES = ['Trade Publication', 'Newspaper', 'Magazine', 'Website / Newsletter', 'Podcast', 'Journalism Organization'] as const;
export const PRESS_REGIONS = ['National', 'New York City', 'Brooklyn', 'Manhattan', 'International'] as const;

/** Add a new outlet (no id) or save changes to an existing one. */
export const savePressOutlet = createServerFn({ method: 'POST' })
  .validator(z.object({
    pass: z.string(), id: z.string().optional(), name: z.string().trim().min(1).max(200),
    contactName: z.string().max(200), contactTitle: z.string().max(200), email: z.string().max(200), website: z.string().max(400),
    type: z.enum(PRESS_TYPES).nullable(), region: z.enum(PRESS_REGIONS).nullable(), status: z.enum(PRESS_STATUSES),
    coverage: z.string().max(5000), notes: z.string().max(5000),
  }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const email = data.email.trim(), site = data.website.trim();
    const vals = {
      name: data.name.trim(), contactName: data.contactName.trim() || null, contactTitle: data.contactTitle.trim() || null,
      email: email ? { email, label: email } : null,
      website: site ? { url: /^https?:/i.test(site) ? site : `https://${site}`, label: site.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '') } : null,
      type: data.type, region: data.region, outreachStatus: data.status, coverage: data.coverage.trim() || null, notes: data.notes.trim() || null,
    };
    return withBoardAuth(async () => {
      const b = new PublicRelationsListBoard();
      if (data.id) await b.item(data.id).update(vals).execute();
      else await b.item().create({ ...vals, removed: false }).execute();
      return { ok: true };
    });
  });

/** Delete an outlet: archive it if allowed, otherwise tick "Removed" so it disappears from the list. */
export const removePressOutlet = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(async () => {
      const b = new PublicRelationsListBoard();
      try { await b.item(data.id).archive().execute(); return { ok: true }; }
      catch (e) { console.warn('[press] archive not allowed, marking removed', e); }
      await b.item(data.id).update({ removed: true }).execute();
      return { ok: true };
    });
  });

export const setPressStatus = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string(), status: z.enum(PRESS_STATUSES) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    await withBoardAuth(() => new PublicRelationsListBoard().item(data.id).update({
      outreachStatus: data.status,
      ...(data.status === 'Not Contacted' ? {} : { lastContacted: new Date(`${today}T12:00:00Z`) }),
    }).execute());
    return { ok: true };
  });
